"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  RememberedTextInput,
  RememberedNamesNote,
  TableScroll,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
} from "@/components/tools/ui";
import { track } from "@/lib/analytics";

/* Training & competence matrix builder.

   A workers × courses/tickets grid with an expiry date per cell. The matrix is
   the record SSIP assessors and principal contractors ask for to evidence the
   CDM 2015 Regulation 8 duty to confirm the people doing the work have the
   skills, knowledge, training and experience for it (verified against the
   rams-knowledge bank, 2026-06-06).

   IMPORTANT FRAMING: records SUPPORT a competence judgement, they do not make
   someone competent. A card or certificate is evidence; competence is the
   responsible person's call. This tool never declares anyone "competent".

   The whole matrix persists in localStorage ("ramsdocs.training-matrix") so it
   becomes a living record the user comes back to. Nothing is sent anywhere. */

const STORE_KEY = "ramsdocs.training-matrix";

/** Default columns — the tickets/training most UK construction sites ask for. */
const DEFAULT_COLUMNS = [
  "CSCS card",
  "Asbestos awareness",
  "Manual handling",
  "Working at height",
  "First aid",
  "Face fit",
];

interface Column {
  id: string;
  name: string;
}

interface Worker {
  id: string;
  name: string;
  /** columnId -> ISO yyyy-mm-dd expiry (or "" / undefined for blank). */
  expiries: Record<string, string>;
}

interface MatrixState {
  columns: Column[];
  workers: Worker[];
}

type CellStatus = "blank" | "expired" | "expiring" | "indate";

const EXPIRING_DAYS = 60;
const MS_PER_DAY = 86_400_000;

let idSeq = 1;
function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${idSeq++}`;
}

function initialState(): MatrixState {
  return {
    columns: DEFAULT_COLUMNS.map((name) => ({ id: newId("c"), name })),
    workers: [{ id: newId("w"), name: "", expiries: {} }],
  };
}

function readState(): MatrixState | null {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      Array.isArray(parsed.columns) &&
      Array.isArray(parsed.workers)
    ) {
      return parsed as MatrixState;
    }
    return null;
  } catch {
    return null;
  }
}

function writeState(state: MatrixState): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    /* storage full / blocked — the matrix still works, it just won't persist */
  }
}

/** Days from today (local, midnight) to the given ISO date. Negative = past. */
function daysUntil(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / MS_PER_DAY);
}

function cellStatus(iso: string | undefined): CellStatus {
  if (!iso) return "blank";
  const days = daysUntil(iso);
  if (days === null) return "blank";
  if (days < 0) return "expired";
  if (days <= EXPIRING_DAYS) return "expiring";
  return "indate";
}

function formatShort(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/* ── .ics generation (reimplemented locally — downloadReviewIcs is not exported).
   One VCALENDAR with an all-day VEVENT per expiring/expired entry, on the expiry
   date, with a VALARM 30 days before (-P30D). */
function icsEscape(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

function buildIcs(entries: { worker: string; course: string; iso: string }[]): string {
  const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//RamsDocs//Training matrix//EN",
    "CALSCALE:GREGORIAN",
  ];
  entries.forEach((e, i) => {
    const day = e.iso.replace(/-/g, "");
    if (!/^\d{8}$/.test(day)) return;
    const summary = icsEscape(`${e.worker} — ${e.course} expires`);
    lines.push(
      "BEGIN:VEVENT",
      `UID:ramsdocs-training-${day}-${i}-${stamp}`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${day}`,
      `SUMMARY:${summary}`,
      "DESCRIPTION:Training / ticket expiry from your RamsDocs training matrix. Arrange renewal before this date.",
      "BEGIN:VALARM",
      "TRIGGER:-P30D",
      "ACTION:DISPLAY",
      `DESCRIPTION:${summary} in 30 days`,
      "END:VALARM",
      "END:VEVENT",
    );
  });
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

export function TrainingMatrixBuilder() {
  const [state, setState] = useState<MatrixState>(() => initialState());
  const [hydrated, setHydrated] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [newColumn, setNewColumn] = useState("");
  const resultTracked = useRef(false);

  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();

  // Hydration-safe: read the saved matrix after mount only.
  useEffect(() => {
    const saved = readState();
    if (saved) setState(saved);
    setHydrated(true);
  }, []);

  // Persist on every change, but only after the initial read (so we don't
  // overwrite a saved matrix with the empty default during hydration).
  useEffect(() => {
    if (hydrated) writeState(state);
  }, [state, hydrated]);

  const { columns, workers } = state;

  const namedWorkers = useMemo(
    () => workers.filter((w) => w.name.trim().length > 0),
    [workers],
  );

  useEffect(() => {
    if (namedWorkers.length > 0 && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("training-matrix", { workers: namedWorkers.length, columns: columns.length });
    }
  }, [namedWorkers.length, columns.length]);

  // Mutators.
  const addWorker = () =>
    setState((s) => ({ ...s, workers: [...s.workers, { id: newId("w"), name: "", expiries: {} }] }));

  const removeWorker = (id: string) =>
    setState((s) => ({ ...s, workers: s.workers.filter((w) => w.id !== id) }));

  const setWorkerName = (id: string, name: string) =>
    setState((s) => ({ ...s, workers: s.workers.map((w) => (w.id === id ? { ...w, name } : w)) }));

  const setCell = (workerId: string, columnId: string, iso: string) =>
    setState((s) => ({
      ...s,
      workers: s.workers.map((w) =>
        w.id === workerId ? { ...w, expiries: { ...w.expiries, [columnId]: iso } } : w,
      ),
    }));

  const addColumn = () => {
    const name = newColumn.trim();
    if (!name) return;
    setState((s) => ({ ...s, columns: [...s.columns, { id: newId("c"), name }] }));
    setNewColumn("");
  };

  const removeColumn = (id: string) =>
    setState((s) => ({
      ...s,
      columns: s.columns.filter((c) => c.id !== id),
      // Drop the dead cells too.
      workers: s.workers.map((w) => {
        const { [id]: _drop, ...rest } = w.expiries;
        return { ...w, expiries: rest };
      }),
    }));

  const clearMatrix = () => {
    try {
      window.localStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
    setState(initialState());
    setConfirmClear(false);
  };

  // Summary across all named workers' cells.
  const summary = useMemo(() => {
    let expired = 0;
    let expiring = 0;
    namedWorkers.forEach((w) => {
      columns.forEach((c) => {
        const st = cellStatus(w.expiries[c.id]);
        if (st === "expired") expired++;
        else if (st === "expiring") expiring++;
      });
    });
    return { expired, expiring };
  }, [namedWorkers, columns]);

  // Entries that need a reminder (expired or expiring within 60 days).
  const reminderEntries = useMemo(() => {
    const out: { worker: string; course: string; iso: string }[] = [];
    namedWorkers.forEach((w) => {
      columns.forEach((c) => {
        const iso = w.expiries[c.id];
        const st = cellStatus(iso);
        if ((st === "expired" || st === "expiring") && iso) {
          out.push({ worker: w.name.trim(), course: c.name, iso });
        }
      });
    });
    return out;
  }, [namedWorkers, columns]);

  const downloadIcs = () => {
    if (reminderEntries.length === 0) return;
    const ics = buildIcs(reminderEntries);
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "training-expiry-reminders.ics";
    a.click();
    URL.revokeObjectURL(url);
    track("tool_training_ics", { entries: reminderEntries.length });
  };

  // Required to print: at least one named worker, plus the evidence basics.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "At least one worker named", done: namedWorkers.length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="space-y-6">
      <div className="sticky top-20 z-10">
        <CompletionPanel items={requiredItems} title="Matrix readiness" />
      </div>

      {/* Framing */}
      <div className="rounded-xl border border-emerald/30 bg-emerald-tint px-5 py-4 text-[0.92rem] leading-relaxed text-emerald-deep">
        <strong>Records support a competence judgement — they don&apos;t make someone competent.</strong> A card
        or certificate is evidence that someone has the relevant training; whether a person is competent for the
        task is the responsible person&apos;s call. This matrix is the record SSIP assessors and principal
        contractors ask for to evidence the CDM 2015 duty to use people with the right skills, knowledge,
        training and experience.
      </div>

      {/* Summary strip */}
      {namedWorkers.length > 0 && (summary.expired > 0 || summary.expiring > 0) && (
        <div
          className={`rounded-xl border px-5 py-4 text-[0.92rem] font-semibold ${
            summary.expired > 0
              ? "border-risk-veryhigh/40 bg-[#fdecec] text-[#a31616]"
              : "border-risk-medium/40 bg-[#fdf6e3] text-[#8a6400]"
          }`}
          aria-live="polite"
        >
          {summary.expired} expired, {summary.expiring} expiring within {EXPIRING_DAYS} days.
        </div>
      )}

      {/* The matrix */}
      <div className="card shadow-soft p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl">Training matrix</h2>
          <span className="text-[0.78rem] text-muted">
            {namedWorkers.length} worker{namedWorkers.length === 1 ? "" : "s"} · {columns.length} column
            {columns.length === 1 ? "" : "s"}
          </span>
        </div>

        <TableScroll>
          <table className="mt-4 w-full min-w-[640px] border-collapse text-[0.85rem]">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="sticky left-0 z-[1] bg-white py-2 pr-3 align-bottom font-semibold text-ink">
                  Worker
                </th>
                {columns.map((c) => (
                  <th key={c.id} className="px-2 py-2 align-bottom font-semibold text-ink">
                    <div className="flex flex-col gap-1">
                      <span className="leading-tight">{c.name}</span>
                      <button
                        type="button"
                        onClick={() => removeColumn(c.id)}
                        className="self-start text-[0.66rem] font-semibold uppercase tracking-wide text-muted hover:text-risk-veryhigh print-hide"
                        aria-label={`Remove column ${c.name}`}
                      >
                        Remove
                      </button>
                    </div>
                  </th>
                ))}
                <th className="w-10 print-hide" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {workers.map((w) => (
                <tr key={w.id} className="border-b border-line/60 align-top">
                  <td className="sticky left-0 z-[1] bg-white py-2 pr-3">
                    <RememberedTextInput
                      store="people"
                      value={w.name}
                      onChange={(e) => setWorkerName(w.id, e.target.value)}
                      placeholder="Worker name"
                      className="min-w-[140px]"
                    />
                  </td>
                  {columns.map((c) => {
                    const iso = w.expiries[c.id] ?? "";
                    const st = cellStatus(iso);
                    const cellCls =
                      st === "expired"
                        ? "border-risk-veryhigh/50 bg-[#fdecec]"
                        : st === "expiring"
                        ? "border-risk-medium/50 bg-[#fdf6e3]"
                        : st === "indate"
                        ? "border-risk-low/50 bg-emerald-tint"
                        : "border-line bg-white";
                    return (
                      <td key={c.id} className="px-2 py-2">
                        <input
                          type="date"
                          value={iso}
                          onChange={(e) => setCell(w.id, c.id, e.target.value)}
                          className={`w-full rounded-lg border px-2 py-1.5 text-[0.82rem] text-ink focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25 ${cellCls}`}
                          aria-label={`${c.name} expiry for ${w.name || "worker"}`}
                        />
                      </td>
                    );
                  })}
                  <td className="px-1 py-2 print-hide">
                    {workers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeWorker(w.id)}
                        className="text-[0.72rem] font-semibold text-muted hover:text-risk-veryhigh"
                        aria-label={`Remove ${w.name || "worker"}`}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[0.78rem] text-muted">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-risk-low/50 bg-emerald-tint" /> In date
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-risk-medium/50 bg-[#fdf6e3]" /> Expiring
            within {EXPIRING_DAYS} days
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-risk-veryhigh/50 bg-[#fdecec]" /> Expired
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-line bg-white" /> Blank (—)
          </span>
        </div>

        {/* Add worker / column controls */}
        <div className="mt-5 flex flex-wrap items-end gap-4 print-hide">
          <button
            type="button"
            onClick={addWorker}
            className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
          >
            + Add worker
          </button>
          <div className="flex items-end gap-2">
            <Field label="Add a column (custom ticket / course)">
              <TextInput
                value={newColumn}
                onChange={(e) => setNewColumn(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addColumn();
                  }
                }}
                placeholder="e.g. SMSTS, IPAF, confined space"
              />
            </Field>
            <button
              type="button"
              onClick={addColumn}
              disabled={!newColumn.trim()}
              className="mb-[1px] inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2.5 text-sm font-semibold text-ink hover:border-ink hover:bg-mist disabled:cursor-not-allowed disabled:opacity-50"
            >
              + Add column
            </button>
          </div>
        </div>

        {/* Persistence note + clear */}
        <p className="mt-5 text-[0.78rem] text-muted">
          The whole matrix is saved on this device — nothing is sent to us. Come back any time to update it.{" "}
          <button
            type="button"
            onClick={() => (confirmClear ? clearMatrix() : setConfirmClear(true))}
            className="font-semibold text-emerald-deep underline hover:text-emerald"
          >
            {confirmClear ? "Really clear the whole matrix?" : "Clear matrix"}
          </button>
        </p>
      </div>

      <RememberedNamesNote stores={["people"]} />

      {/* Calendar reminders */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Expiry reminders</h2>
        <p className="mt-1 text-sm text-muted">
          Download one calendar file with a reminder for every expired or soon-to-expire entry. Each event lands
          on the expiry date with an alert 30 days before — import it into Outlook, Google or Apple Calendar.
          Nothing is uploaded.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={downloadIcs}
            disabled={reminderEntries.length === 0}
            className="inline-flex items-center gap-2 rounded-full border border-line-2 px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink hover:bg-mist disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            Download expiry reminders (.ics)
          </button>
          <span className="text-[0.82rem] text-muted">
            {reminderEntries.length === 0
              ? "No expired or soon-to-expire entries to remind on."
              : `${reminderEntries.length} reminder${reminderEntries.length === 1 ? "" : "s"} ready.`}
          </span>
        </div>
      </div>

      {/* Document control */}
      <EvidenceFields
        value={evidence}
        onChange={setEvidence}
        onClear={clearEvidence}
        heading="Record control"
        intro="Who keeps this matrix current and when it was last reviewed — the detail an assessor checks first."
      />

      <GuardedPrintButton
        tool="training-matrix"
        label="Print / save matrix as PDF"
        missing={missingRequired}
        warningTitle="This training matrix is missing the basics"
      />

      {/* Printable matrix */}
      <div className="print-area card shadow-soft p-6" id="training-matrix-doc">
        <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
          Training &amp; competence matrix
        </div>
        <h2 className="mt-1 text-lg">{evidence.company || "Company"} — training matrix</h2>

        {(summary.expired > 0 || summary.expiring > 0) && namedWorkers.length > 0 && (
          <p className="mt-3 text-[0.85rem] font-semibold text-ink">
            {summary.expired} expired, {summary.expiring} expiring within {EXPIRING_DAYS} days.
          </p>
        )}

        {namedWorkers.length === 0 ? (
          <p className="mt-5 text-sm text-muted">Add at least one worker above to build the matrix record.</p>
        ) : (
          <TableScroll>
            <table className="mt-4 w-full min-w-[640px] border-collapse text-[0.8rem]">
              <thead>
                <tr className="border-b border-line text-left">
                  <th className="py-2 pr-3 font-semibold text-ink">Worker</th>
                  {columns.map((c) => (
                    <th key={c.id} className="px-2 py-2 font-semibold text-ink">{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {namedWorkers.map((w) => (
                  <tr key={w.id} className="border-b border-line/60 align-top">
                    <td className="py-2 pr-3 font-semibold text-ink">{w.name.trim()}</td>
                    {columns.map((c) => {
                      const iso = w.expiries[c.id] ?? "";
                      const st = cellStatus(iso);
                      const cellCls =
                        st === "expired"
                          ? "bg-[#fdecec] text-[#a31616]"
                          : st === "expiring"
                          ? "bg-[#fdf6e3] text-[#8a6400]"
                          : st === "indate"
                          ? "bg-emerald-tint text-emerald-deep"
                          : "text-muted";
                      const label =
                        st === "blank"
                          ? "—"
                          : st === "expired"
                          ? `Expired ${formatShort(iso)}`
                          : st === "expiring"
                          ? `Expires ${formatShort(iso)}`
                          : `✓ ${formatShort(iso)}`;
                      return (
                        <td key={c.id} className={`px-2 py-2 ${cellCls}`}>
                          {label}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}

        <p className="mt-5 text-[0.78rem] leading-relaxed text-muted">
          This matrix records training, cards and tickets and their expiry dates to support competence
          decisions. It does not certify that anyone is competent — competence for a given task is a judgement
          for the responsible person, taking account of skills, knowledge, training and experience. Keep cards
          and certificates on file as the underlying evidence.
        </p>

        <EvidencePrint value={evidence} briefing={false} />

        <PrintFooter toolName="training matrix builder" />
      </div>
    </div>
  );
}
